import { useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button, Form, ListGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faLock, faCircleInfo, faKey } from '@fortawesome/free-solid-svg-icons';
import { useLoginMutation, useGetSystemVersionMutation, useLoginWithGoogleMutation } from '../../../features/authentication/authApiSlice';
import { setCredentials } from '../../../features/authentication/authSlice';
import { useDispatch } from 'react-redux';
import { useEffect } from 'react';
import HesSpinner from '../../../components/loadingOverlay/hesSpinner';
import { useLoginContext } from '../context';
import Modal from '../../../components/Modal/index';
import { useGoogleLogin } from '@react-oauth/google';
import appConfig from  '../../../app-config';

function Login() {
  const location = useLocation();
  const userNameRef = useRef();
  const passwordRef = useRef();
  const supportCodeRef = useRef();
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);
  const [showNoteMessage, setShowNoteMessage] = useState(false);
  const [login, { isLoading }] = useLoginMutation();
  const [systemVersionData] = useGetSystemVersionMutation();
  const [loginWithGoogle] = useLoginWithGoogleMutation();
  const [versionNumber, setVersionNumber] = useState('');
  const dispatch = useDispatch();
  const loginContext = useLoginContext();
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false);
  const NYCLogin = () => {
    window.location.href =  'https://sso.castlelearning.com/sso/redirect_uri?iss=https://sso.harriscomputer.io/realms/hes&auth_request_params=kc_idp_hint=nycdoe&target_link_uri='+ appConfig.nycRedirectUrl;
  };
  const nycSSOError = location.state?.nycSsoResponse;
  const env = process.env.REACT_APP_ENV;
  useEffect(() => {
    const rememberMeStorage = localStorage.getItem('rememberMe');
    const firstTimeVistStorage = localStorage.getItem('firstTimeVisit');

    if (firstTimeVistStorage == undefined) setShowFirstTimeModal(true);

    if (rememberMeStorage) {
      setRememberMe(true);
      userNameRef.current.value = rememberMeStorage;
    }

    try {
      async function getVersion() {
        var version = await systemVersionData().unwrap();
        setVersionNumber(version);
      }
      getVersion();
    } catch {
      console.log('Couldn\'t get version number');
    }
  }, []);

  // LOGIN
  async function handleSubmit(e) {
    e.preventDefault();
    loginContext.setErrorMessage('');
    localStorage.removeItem('classes');

    if(['testing', 'rc'].includes(env) && supportCodeRef.current.value !== appConfig.castleSupportCode)
    {
      loginContext.setErrorMessage('Support Code is incorrect');
      return;
    }

    try {
      const authResponse = await login({
        UserName: userNameRef.current.value,
        Password: passwordRef.current.value
      }).unwrap();

      dispatch(
        setCredentials({
          accessToken: authResponse.accessToken
        })
      );

      if (rememberMe) {
        localStorage.setItem('rememberMe', userNameRef.current.value);
      } else {
        localStorage.removeItem('rememberMe');
      }

      if (!authResponse.isSuccess && authResponse.reason == 'SuperAdminAccessRequired') {
        navigate('superAdminLogin');
      } else {
        navigate('/dashboard');
      }
    } catch (ex) {
      if (!ex?.status || ex.status === 'FETCH_ERROR') {
        loginContext.setErrorMessage('No server response');
      } else if (ex.data.reason) {
        loginContext.setErrorMessage(ex.data?.reason);
      } else {
        loginContext.setErrorMessage('Login was unsuccessful');
      }
    }
  }

  const handleGoogleLogin = useGoogleLogin({
    onSuccess: async response => {
      try {
        const authResponse = await loginWithGoogle(response.access_token).unwrap();
        dispatch(
          setCredentials({
            accessToken: authResponse.accessToken
          })
        );
        if (!authResponse.isSuccess && authResponse.reason == 'SuperAdminAccessRequired') {
          navigate('superAdminLogin');
        } else {
          navigate('/dashboard');
        }
      } catch (ex) {
        if (!ex?.status || ex.status === 'FETCH_ERROR') {
          loginContext.setErrorMessage('No server response');
        } else if (ex?.data?.reason) {
          loginContext.setErrorMessage(ex.data?.reason);
        } else {
          loginContext.setErrorMessage('Login was unsuccessful');
        }
      }

    },
    onError: () => loginContext.setErrorMessage('Failed to login with Google account.')
  });

  return (
    <>
      <ListGroup className="login-card-body" variant="flush">
        <Form onSubmit={handleSubmit}>
          <ListGroup.Item>
            <Form.Group id="userName">
              <Form.Label>ID or Email</Form.Label>
              <div>
                <Form.Control className="loginTextBox" type="text" ref={userNameRef} required />
                <FontAwesomeIcon className="loginTextboxIcon" icon={faEnvelope} />
              </div>
            </Form.Group>
          </ListGroup.Item>

          <ListGroup.Item>
            <Form.Group id="password">
              <Form.Label>Password</Form.Label>
              <Form.Control className="loginTextBox" type="password" ref={passwordRef} required />
              <FontAwesomeIcon className="loginTextboxIcon" icon={faLock} />
            </Form.Group>
          </ListGroup.Item>

          {['testing', 'rc'].includes(env) &&
            <ListGroup.Item>
              <Form.Group id="supportCode">
                <Form.Label>Support Code</Form.Label>
                <Form.Control className="loginTextBox" type="password" ref={supportCodeRef} required />
                <FontAwesomeIcon className="loginTextboxIcon" icon={faKey} />
              </Form.Group>
            </ListGroup.Item>
          }

          <ListGroup.Item>
            <div className="d-flex flex-row justify-content-between">
              <Form.Check id="rembmerMeCheckBox">
                <Form.Check.Input
                  type="checkbox"
                  defaultChecked={rememberMe}
                  onClick={() => setRememberMe(!rememberMe)}
                />
                <Form.Check.Label className="ms-2 mt-0">Remember Me</Form.Check.Label>
              </Form.Check>
              <span
                className="forgotPasswordText"
                onClick={() => { navigate('forgotPassword'); loginContext.setErrorMessage(''); }}>
                Forgot/Reset Password
              </span>
            </div>
          </ListGroup.Item>
          <ListGroup.Item>

            <Button disabled={isLoading} variant='castleRed' className='d-flex flex-row justify-content-center submitButton align-items-center' type='submit'>
              {isLoading && <div style={{ position: 'absolute', top: '14px', bottom: '14px', left: '32%' }}><HesSpinner /></div>}
              {isLoading ? 'Signing in...' : 'Sign In'}
            </Button>
          </ListGroup.Item>
        </Form>

        {loginContext.errorMessage && (
          <ListGroup.Item>
            <div className="d-flex flex-column align-content-center text-center">
              <div className='mx-auto error'>
                {loginContext.errorMessage}
              </div>
              <div className='mx-auto text-color' style={{ textAlign: 'center' }}>
                Please try again, use the Forgot/Reset Password link, or contact customer support at
                800-345-7607
              </div>
            </div>
          </ListGroup.Item>
        )}

        <ListGroup.Item>
          <div className="d-flex flex-row justify-content-between align-items-center">
            <div style={{ borderBottom: '1px solid #A6A6A6', height: '1px', width: '100%' }}></div>
            <div style={{ width: '10%', textAlign: 'center', margin: '0 1em', fontSize: '16px' }}>
              or
            </div>
            <div style={{ borderBottom: '1px solid #A6A6A6', height: '1px', width: '100%' }}></div>
          </div>
        </ListGroup.Item>

        <ListGroup.Item>
          <Button disable={isLoading} type="button" className="d-flex flex-row justify-content-center align-items-center googleButton w-100" onClick={() => handleGoogleLogin()}>
            <i className="fab fa-google" style={{ fontSize: '1.85em' }}></i>
            <span className='text-color fs-16 mx-2'>
              Sign in with Google
            </span>
          </Button>
        </ListGroup.Item>
        <ListGroup.Item>
          <Button disable={isLoading} type="button" className="googleButton w-100" onClick={NYCLogin}>
            <span className='text-color fs-16 mx-2'>
            Sign in with NYC public school
            </span>
          </Button>
        </ListGroup.Item>
        {nycSSOError && nycSSOError.length>0  && (
          <ListGroup.Item>
            <div className="d-flex flex-column align-content-center text-center">
              <div className='mx-auto error'>
                {nycSSOError}
              </div>
            </div>
          </ListGroup.Item>
        )}

        <ListGroup.Item>
          <div className="w-100">
            <Button
              type="button"
              className="noteButton"
              onClick={() => setShowNoteMessage(!showNoteMessage)}>
              <FontAwesomeIcon icon={faCircleInfo} style={{ color: '#E47B00' }} />
              <span style={{ color: '#E47B00', opacity: '0.8', marginLeft: '0.5em' }}>Note</span>
            </Button>

            <div className={showNoteMessage ? 'noteMessage' : 'noteMessage hidden'}>
              For security reasons you should close the browser after logging out. When using the
              above login buttons the browser may remain logged into the selected service. This will
              allow the next user to gain access to your Castle account. If you use the Google
              Sign-In please read this{' '}
              <a href="https://support.google.com/accounts/answer/54490?hl=en&visit_id=638528858339456032-678042021&rd=1" className="link-primary" style={{ textDecoration: 'none' }} target='_blank'>
                page
              </a>{' '}
              for details on how to configure your browser to prevent saving of your Google account
              login.
            </div>
          </div>
          <div onClick={() => setShowNoteMessage(false)} style={{ display: (showNoteMessage ? 'inline-block' : 'none'), left: 0, top: 0, position: 'fixed', height: '100vh', width: '100vw', zIndex: '1' }}></div>

        </ListGroup.Item>
        <ListGroup.Item>
          {versionNumber && <div className='text-center'>{`V.${versionNumber}`}</div>}
        </ListGroup.Item>
        <ListGroup.Item style={{ height: '3vh' }}>
        </ListGroup.Item>
      </ListGroup>

      <Modal
        isOpen={showFirstTimeModal}
        onClose={() => { localStorage.setItem('firstTimeVisit', false); setShowFirstTimeModal(false); }}
        titleName='Welcome!'
        contentWidth='60%'>
        <div className='d-flex flex-column'>
          <div>Oh hi, Castle Community! Noticed the fresh new look? Don't fret, it's still us – just upgraded! We know you're going to love it!</div>
          <div className='d-flex flex-row justify-content-end mt-4'><Button variant='castleRed' className='w-25' onClick={() => { localStorage.setItem('firstTimeVisit', false); setShowFirstTimeModal(false); }}>Ok</Button></div>
        </div>
      </Modal>
    </>
  );
}


export default Login;
